import { z } from "zod";
import {
  careOfDeliverySchema,
  consigneeAndDeliverySchema,
  shipperSchema,
} from "../../services/prePlanningService.types";
import { tagSchema } from "../freight/tag.type";

export type ReasonCodes = {
  id: number;
  name: string;
  description: string | null;
};

export const osadAssignmentSchema = z.object({
  reasonId: z.number(),
  moduleId: z.number().nullish(),
  shipmentIds: z.array(z.number()),
});

export type OsadAssignment = z.infer<typeof osadAssignmentSchema>;

const osadShipmentTagSchema = tagSchema.extend({
  bills: z.number(),
  color: z.string(),
});

const mappedOsadTagSchema = osadShipmentTagSchema.transform((input) => ({
  ...input,
  colorHex: input.color,
}));

const billsByTagSchema = tagSchema.omit({ description: true }).extend({
  bills: z.number(),
  color: z.string(),
});

const mappedBillsByTagSchema = billsByTagSchema.transform((input) => ({
  ...input,
  colorHex: input.color,
}));

const osadCurrentLocationSchema = z.object({
  type: z.string(),
  name: z.string(),
  trailer: z.string().nullish(),
  pullId: z.number().nullish(),
  trapId: z.number().nullish(),
  tripNumber: z.number().nullish(),
});

const osadSummarySchema = z.object({
  areaName: z.string(),
  bills: z.number(),
  pallets: z.number(),
  pieces: z.number(),
  weight: z.number(),
  handlingUnits: z.number(),
  billsByTags: z.array(mappedBillsByTagSchema),
});

const osadShipmentSchema = z.object({
  shipmentId: z.number(),
  proNumber: z.string(),
  reasonId: z.number().nullish(),
  reasonCode: z.string(),
  pallets: z.number(),
  pieces: z.number(),
  weight: z.number(),
  weightUnit: z.string(),
  handlingUnits: z.number(),
  tags: z.array(mappedOsadTagSchema),
  originSic: z.string().nullish(),
  destinationSic: z.string().nullish(),
  finalDestinationSic: z.string(),
  status: z.string(),
  dueServiceDate: z.date().nullish(),
  loadToSicId: z.number().nullish(),
  loadTo: z.string().nullish(),
  currentLocation: osadCurrentLocationSchema,
  appointmentStartTimestampUtc: z.date().nullish().optional(),
  appointmentEndTimestampUtc: z.date().nullish().optional(),
  shipper: shipperSchema.optional(),
  consignee: consigneeAndDeliverySchema.nullish().optional(),
  careOfDelivery: careOfDeliverySchema.nullish().optional(),
  deliveryAddress: consigneeAndDeliverySchema.nullish().optional(),
  isPlannedOB: z.boolean(),
});

const mappedOsadShipmentSchema = osadShipmentSchema.transform((input) => ({
  ...input,
  destinationSicCode: input.destinationSic || null,
  serviceDueDate: input.dueServiceDate ? new Date(input.dueServiceDate) : null,
  appointmentDate: input.appointmentStartTimestampUtc || null,
  appointmentEndDate: input.appointmentEndTimestampUtc || null,
}));

export const osadShipmentsSchema = z.object({
  summary: osadSummarySchema,
  shipments: z.array(mappedOsadShipmentSchema),
});

export type OSADShipment = z.infer<typeof mappedOsadShipmentSchema>;
export type OSADShipments = z.infer<typeof osadShipmentsSchema>;
